import { EditorEvents, useCurrentEditor } from '@tiptap/react';
import { useEffect, memo, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { AlertEvents, AlertTransactions } from './plugins/Alert';

function AlertTooltip() {
  const { editor } = useCurrentEditor();
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [coords, setCoords] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!editor) return;

    function handleTransaction({ transaction }: EditorEvents['transaction']) {
      const warningMeta: AlertEvents['warning'] | undefined = transaction.getMeta(
        AlertTransactions.WARNING,
      );
      if (warningMeta) {
        const { message, coords } = warningMeta;
        setMessage(message);
        setCoords(coords);
        setIsVisible(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, 3000);
      }
    }
    editor.on('transaction', handleTransaction);
    return () => {
      editor.off('transaction', handleTransaction);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [editor]);

  useEffect(() => {
    function hideTooltipWhenTransactionHappens() {
      setIsVisible(false);
    }
    if (!editor) {
      return;
    }

    if (!isVisible) {
      editor.off('transaction', hideTooltipWhenTransactionHappens);
      return;
    }
    editor.on('transaction', hideTooltipWhenTransactionHappens);
    return () => {
      editor.off('transaction', hideTooltipWhenTransactionHappens);
    };
  }, [editor, isVisible]);

  if (!editor || !isVisible) return null;

  return (
    <div
      className="flex items-center gap-2 font-medium transition-all bg-warning-50/90 text-warning-800 rounded-full px-2 py-1 fixed z-50 alert-tooltip -translate-y-[180%] -translate-x-[18px] pointer-events-none"
      style={{
        left: `${coords.right}px`,
        top: `${coords.top + window.scrollY}px`,
      }}
    >
      <FontAwesomeIcon icon={faCircleExclamation} />
      {message}
    </div>
  );
}

export default memo(AlertTooltip);
